import React, { useState } from 'react'
import { useTheme } from '@mui/material/styles'
import { getCreditCardIcon } from '@/v2/utils/credit-card'
import {
  usePatientFinancePaymentMethods,
  usePaymentMethods,
} from '@/v2/hooks/usePatientFinance'
import { useUser } from '@/v2/hooks/useUser'
import Typography from '@/v2/components/common/Typography'
import Box from '@mui/material/Box'
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined'
import MoveToInboxOutlinedIcon from '@mui/icons-material/MoveToInboxOutlined'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import IconMenu from '@/v2/components/common/IconMenu'
import Dialog from '@mui/material/Dialog'
import Button from '@/v2/components/common/Button'
import { Grid } from '@mui/material'
import styled from 'styled-components'

const Wrapper = styled.div`
  padding: 38px 30px 23px 30px;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  text-align: center;
  gap: 10px;
`

const SavedCard = ({ card, patient, setAction }) => {
  const theme = useTheme()
  const { user } = useUser()
  // Archiving a card is restricted to the master account, not front desk staff
  const canArchive = user?.admin || false
  const { removeInvoicesPaymentMethod } = usePaymentMethods()
  const [showArchiveConfirmation, setShowArchiveConfirmation] = useState(false)

  const { refetch } = usePatientFinancePaymentMethods({
    patientId: patient.id,
  })

  const handleArchive = async () => {
    await removeInvoicesPaymentMethod({
      patientId: patient.id,
      paymentMethodId: card.id,
    })

    await refetch()

    setAction('delete')
    setShowArchiveConfirmation(false)
  }

  const handleArchiveClick = () => {
    setShowArchiveConfirmation(true)
  }

  const menuOptions = [
    {
      label: (
        <Box display="flex" alignItems="center">
          <MoveToInboxOutlinedIcon sx={{ color: theme.palette.gray.dark }} />
          <Typography
            variant="body3"
            color={theme.palette.gray.dark}
            sx={{ marginLeft: '8px', fontSize: '14px' }}
          >
            Archive
          </Typography>
        </Box>
      ),
      onClick: handleArchiveClick,
    },
  ]

  return (
    <>
      <Box px={4} py={3} borderRadius={2} bgcolor={'gray.light'}>
        <Box
          display={'flex'}
          flexDirection={'row'}
          justifyContent={'space-between'}
          alignItems={'center'}
        >
          <Box>{getCreditCardIcon(card.brand)}</Box>

          <Box>
            <Typography variant="h3">**** **** **** {card.last4}</Typography>
            <Typography
              variant="body3"
              color={theme.palette.gray.dark}
              lineHeight="16px"
            >
              Expires {card.expirationDate}
            </Typography>
            <br />
            {card.status === 'removed' && (
              <Typography
                variant="body3"
                color={theme.palette.error.main}
                lineHeight="16px"
              >
                This card was archived.
              </Typography>
            )}
            {card.status === 'expired' && (
              <Typography
                variant="body3"
                color={theme.palette.error.main}
                lineHeight="16px"
              >
                This card has expired.
              </Typography>
            )}
          </Box>

          {card.status === 'active' ? (
            canArchive && (
              <Box>
                <IconMenu
                  options={menuOptions}
                  icon={
                    <MoreVertIcon
                      fontSize="small"
                      sx={{ color: theme.palette.gray.dark }}
                    />
                  }
                  aria-label="More"
                  title="More"
                />
              </Box>
            )
          ) : (
            <Box>
              <ReportProblemOutlinedIcon
                sx={{ color: theme.palette.gray.dark }}
              />
            </Box>
          )}
        </Box>
      </Box>

      <Dialog
        open={showArchiveConfirmation}
        onClose={() => setShowArchiveConfirmation(false)}
      >
        <Wrapper>
          <Typography variant="h3" sx={{ marginBottom: '14px' }}>
            Are you sure you want to archive this card?
          </Typography>
          <Grid
            display="flex"
            width={'100%'}
            justifyContent={'flex-end'}
            gap={1}
          >
            <Button
              variant="text"
              color="secondary"
              onClick={() => setShowArchiveConfirmation(false)}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={handleArchive}
              sx={{
                color: '#fff',
              }}
            >
              Archive
            </Button>
          </Grid>
        </Wrapper>
      </Dialog>
    </>
  )
}

export default SavedCard
